import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Grid
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	btnWrapper: {
		position: "relative"
	},
	buttonProgress: {
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12
	},
	formControl: {
		margin: theme.spacing(1, 0),
		minWidth: "100%"
	}
}));

const SolicitacaoSchema = Yup.object().shape({
	typeId: Yup.string().required("Obrigatório")
});

const SolicitacaoModal = ({ open, onClose, ticketId }) => {
	const classes = useStyles();
	const [types, setTypes] = useState([]);
	const [loadingTypes, setLoadingTypes] = useState(false);

	const initialState = {
		typeId: "",
		observation: ""
	};

	useEffect(() => {
		const fetchTypes = async () => {
			setLoadingTypes(true);
			try {
				// ticketId (nao whatsappId): o backend le a conexao do proprio
				// ticket, com a empresa do JWT, e devolve so as demandas
				// daquela linha - ver ListRequestTypesProxyService. Mandar a
				// linha daqui deixaria o escopo trocavel pela URL.
				const { data } = await api.get("/solicitacoes/tipos", {
					params: { ticketId },
				});
				setTypes(data);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingTypes(false);
			}
		};

		if (open) {
			fetchTypes();
		}
	}, [open, ticketId]);

	const handleClose = () => {
		onClose();
	};

	const handleSaveSolicitacao = async (values, { resetForm }) => {
		try {
			const { data } = await api.post("/solicitacoes", {
				ticketId,
				typeId: values.typeId,
				observation: values.observation || undefined
			});
			toast.success(
				i18n.t("solicitacaoModal.success", { number: data.number })
			);
			resetForm();
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>{i18n.t("solicitacaoModal.title")}</DialogTitle>
			<Formik
				initialValues={initialState}
				enableReinitialize
				validationSchema={SolicitacaoSchema}
				onSubmit={handleSaveSolicitacao}
			>
				{({ touched, errors, isSubmitting, values, handleChange }) => (
					<Form>
						<DialogContent dividers>
							<Grid container spacing={1}>
								<Grid item xs={12}>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										error={touched.typeId && Boolean(errors.typeId)}
									>
										<InputLabel id="solicitacao-type-label">
											{i18n.t("solicitacaoModal.form.type")}
										</InputLabel>
										<Select
											labelId="solicitacao-type-label"
											name="typeId"
											value={values.typeId}
											onChange={handleChange}
											label={i18n.t("solicitacaoModal.form.type")}
											disabled={loadingTypes}
										>
											{types.map(type => (
												<MenuItem key={type.id} value={type.id}>
													{type.name}
												</MenuItem>
											))}
										</Select>
										{/* Antes do escopo por conexao a lista nunca vinha
										    vazia, entao um dropdown mudo sempre foi bug. Agora
										    e um estado legitimo: a linha deste ticket pode nao
										    ter nenhuma demanda vinculada. Sem esta mensagem o
										    atendente ve um campo que nao abre e nao entende. */}
										{!loadingTypes && types.length === 0 && (
											<FormHelperText id="solicitacao-sem-tipos">
												{i18n.t("solicitacaoModal.form.semTiposParaConexao")}
											</FormHelperText>
										)}
									</FormControl>
								</Grid>
								<Grid item xs={12}>
									<Field
										as={TextField}
										label={i18n.t("solicitacaoModal.form.observation")}
										name="observation"
										multiline
										minRows={3}
										fullWidth
										variant="outlined"
										margin="dense"
									/>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="secondary"
								disabled={isSubmitting}
								variant="outlined"
							>
								{i18n.t("solicitacaoModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting || loadingTypes}
								variant="contained"
								className={classes.btnWrapper}
							>
								{i18n.t("solicitacaoModal.buttons.create")}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default SolicitacaoModal;
